// UserDashboard.js
import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);

const UserDashboard = () => {
  const [stats, setStats] = useState(null);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState([]);
  const { user, logout } = useAuth();

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  // Update availableYears when stats changes
  useEffect(() => {
    if (stats?.availableYears && Array.isArray(stats.availableYears) && stats.availableYears.length > 0) {
      setAvailableYears(stats.availableYears);
    } else if (stats?.expenses && Array.isArray(stats.expenses) && stats.expenses.length > 0) {
      const years = [...new Set(stats.expenses.map(e => e.year).filter(y => y != null && !isNaN(y) && y > 1900 && y < 2100))].sort((a, b) => b - a);
      if (years.length > 0) {
        setAvailableYears(years);
      }
    }
  }, [stats]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Prepare params object, only include year/month if they are not null
      const params = {};
      if (year !== null) params.year = year;
      if (month !== null) params.month = month;
      
      const response = await api.get('/dashboard/user-stats', {
        params
      });
      setStats(response.data);
      
      // Set availableYears separately to ensure it's always available
      if (response.data?.availableYears && Array.isArray(response.data.availableYears)) {
        setAvailableYears(response.data.availableYears);
      } else if (response.data?.expenses && Array.isArray(response.data.expenses)) {
        // Fallback: extract from expenses
        const years = [...new Set(response.data.expenses.map(e => e.year).filter(y => y != null && !isNaN(y) && y > 1900 && y < 2100))].sort((a, b) => b - a);
        setAvailableYears(years);
      }
    } catch (error) {
      console.error('Stats olishda xatolik:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPieChartData = () => {
    if (!stats?.expenseSummary) return null;

    const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'];
    
    return {
      labels: stats.expenseSummary.map(item => item.region),
      datasets: [{
        data: stats.expenseSummary.map(item => parseFloat(item.total_amount)),
        backgroundColor: colors.slice(0, stats.expenseSummary.length),
        borderColor: colors.slice(0, stats.expenseSummary.length),
        borderWidth: 1
      }]
    };
  };

  const getBarChartData = () => {
    if (!stats) return null;

    return {
      labels: ['Plan (Sizning Budjetingiz 1.5%)', 'Fact (Umumiy Xarajat)'],
      datasets: [{
        label: 'Summa (so\'m)',
        data: [stats.userShare || 0, stats.totalExpenses || 0],
        backgroundColor: ['#28a745', '#dc3545'],
        borderColor: ['#28a745', '#dc3545'],
        borderWidth: 1
      }]
    };
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Yuklanmoqda...</div>;
  }

  if (!user) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Foydalanuvchi ma'lumotlari topilmadi</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
          <div>
            <h1>{user.role === 'region_manager' ? 'Region Manager' : 'Product Manager'} Dashboard</h1>
            <p>Xush kelibsiz, {user?.fullName || 'Foydalanuvchi'}</p>
            {user?.regions && (
              <p>Regionlaringiz: {user.regions.join(', ')}</p>
            )}
          </div>
        <button
          onClick={logout}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Chiqish
        </button>
      </div>

      <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h3>Filtrlar</h3>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <select
            value={year === null ? 'all' : year}
            onChange={(e) => setYear(e.target.value === 'all' ? null : parseInt(e.target.value))}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="all">Barcha Yillar</option>
            {availableYears.length > 0 ? (
              availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))
            ) : (
              <>
                <option value={2026}>2026</option>
                <option value={2025}>2025</option>
              </>
            )}
          </select>
          <select
            value={month === null ? 'all' : month}
            onChange={(e) => setMonth(e.target.value === 'all' ? null : parseInt(e.target.value))}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="all">Barcha Oylar</option>
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}-oy</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => { setYear(null); setMonth(null); }}
            title="Filtrlarni tozalash"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '18px',
              border: '1px solid #ddd',
              backgroundColor: '#fff',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
      </div>

      {stats && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ padding: '1.5rem', backgroundColor: '#e8f5e8', borderRadius: '8px' }}>
              <h4>Umumiy Savdo</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                {stats.totalIncome.toLocaleString()} so'm
              </p>
            </div>

            <div style={{ padding: '1.5rem', backgroundColor: '#e8f0ff', borderRadius: '8px' }}>
              <h4>Sizning Budjetingiz (1.5%)</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                {stats.userShare.toLocaleString()} so'm
              </p>
            </div>

            <div style={{ padding: '1.5rem', backgroundColor: '#ffe8e8', borderRadius: '8px' }}>
              <h4>Umumiy Xarajat</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                {stats.totalExpenses.toLocaleString()} so'm
              </p>
            </div>

            <div style={{ padding: '1.5rem', backgroundColor: stats.remainingAmount >= 0 ? '#e8f5e8' : '#ffe8e8', borderRadius: '8px' }}>
              <h4>Qolgan Summa</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: stats.remainingAmount >= 0 ? '#28a745' : '#dc3545' }}>
                {stats.remainingAmount.toLocaleString()} so'm
              </p>
            </div>

            <div style={{ padding: '1.5rem', backgroundColor: '#e8f5e8', borderRadius: '8px' }}>
              <h4>O'tkan oy holati</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: stats.lastMonthRemainingAmount >= 0 ? '#28a745' : '#dc3545' }}>
                {stats.lastMonthRemainingAmount.toLocaleString()} so'm
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3>Regionlar bo'yicha xarajatlar (Pie Chart)</h3>
              <div style={{ height: '300px' }}>
                {getPieChartData() && (
                  <Pie 
                    data={getPieChartData()} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom'
                        },
                        datalabels: {
                          display: true,
                          color: '#000',
                          font: {
                            weight: 'bold',
                            size: 12
                          },
                          formatter: (value, context) => {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = ((value / total) * 100).toFixed(1);
                            return percentage + '%';
                          }
                        }
                      }
                    }}
                  />
                )}
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3>Plan vs Fact (Bar Chart)</h3>
              <div style={{ height: '300px' }}>
                {getBarChartData() && (
                  <Bar 
                    data={getBarChartData()} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        datalabels: {
                          display: true,
                          color: '#000',
                          font: {
                            weight: 'bold',
                            size: 12
                          },
                          formatter: (value) => {
                            return value.toLocaleString() + ' so\'m';
                          },
                          anchor: 'end',
                          align: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3>Barcha Xarajatlar</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Sana</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Summa</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Turi</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Tulov Turi</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Comment</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Region</th>
                    </tr>
                  </thead>
                  <tbody>
                {stats?.expenses?.map(expense => (
                  <tr key={expense.id}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      {expense.day}/{expense.month}/{expense.year}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      {parseFloat(expense.amount || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{expense.expense_type || 'Noma\'lum'}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{expense.tulov_turi || 'Noma\'lum'}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{expense.comment || ''}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{expense.region || 'Noma\'lum'}</td>
                  </tr>
                ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3>Regionlar bo'yicha umumiy xarajat</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Region</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Umumiy Summa</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Soni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.expenseSummary?.map(summary => (
                      <tr key={summary.region}>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{summary.region || 'Noma\'lum'}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                          {parseFloat(summary.total_amount || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{summary.count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserDashboard;


